import React, { useState, Component, useMemo } from 'react';
/**
 * VibeCoding Studio Environment
 * Professional AI-assisted development workspace
 */
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Play, Code, Layout, Eye, Terminal, Plus, Zap, MessageSquare, Layers, Sparkles, Check, Copy, PanelLeft, Minimize2 } from 'lucide-react';
import { useTheme } from '../../../../core/contexts/ThemeContext';
interface VibeCodingStudioAppProps {
  onBack: () => void;
}
export function VibeCodingStudioApp({
  onBack
}: VibeCodingStudioAppProps) {
  const {
    theme: currentTheme
  } = useTheme();
  const [activeView, setActiveView] = useState<'design' | 'code' | 'preview'>('design');
  const [selectedComponent, setSelectedComponent] = useState<string>('Button');
  const [showAIChat, setShowAIChat] = useState(false);
  // NIEUW: Status voor het verbergen van de linkerzijbalk
  const [showLeftSidebar, setShowLeftSidebar] = useState(true);
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';
  const views = useMemo(() => [{
    id: 'design' as const,
    label: 'Design',
    icon: Layout,
    color: 'from-purple-500 to-violet-500',
    accent: 'violet'
  }, {
    id: 'code' as const,
    label: 'Code',
    icon: Code,
    color: 'from-emerald-500 to-teal-500',
    accent: 'emerald'
  }, {
    id: 'preview' as const,
    label: 'Preview',
    icon: Eye,
    color: 'from-blue-500 to-cyan-500',
    accent: 'cyan'
  }], []);
  const components = useMemo(() => [{
    name: 'Button',
    type: 'component',
    status: 'ready',
    variants: 3,
    description: 'Interactieve knop'
  }, {
    name: 'Card',
    type: 'component',
    status: 'ready',
    variants: 2,
    description: 'Content container'
  }, {
    name: 'Input',
    type: 'component',
    status: 'ready',
    variants: 4,
    description: 'Formulier invoer'
  }, {
    name: 'Modal',
    type: 'component',
    status: 'ready',
    variants: 2,
    description: 'Overlay'
  }], []);
  const aiSuggestions = useMemo(() => [{
    type: 'optimization',
    title: 'Performance',
    description: 'Memoize expensive calculations',
    impact: 'high',
    color: 'emerald',
    icon: Zap
  }, {
    type: 'accessibility',
    title: 'A11y',
    description: 'Add ARIA labels to interactive elements',
    impact: 'medium',
    color: 'blue',
    icon: Check
  }, {
    type: 'refactor',
    title: 'Code Quality',
    description: 'Extract repeated logic to custom hook',
    impact: 'medium',
    color: 'purple',
    icon: Sparkles
  }], []);
  const codeExample = `import React from "react";
import { motion } from "framer-motion";
// Fix: Added ARIA label for better accessibility
export function Button({ 
  children, 
  variant = "primary",
  onClick 
}) {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      aria-label={\`\${children} button\`} 
      className={\`btn btn-\${variant}\`}
    >
      {children}
    </motion.button>
  );
}`;

  // Left Sidebar Component (Helper)
  const LeftSidebar = () => <motion.div initial={{
    width: 0,
    opacity: 0
  }} animate={{
    width: showLeftSidebar ? 256 : 0,
    opacity: showLeftSidebar ? 1 : 0
  }} transition={{
    duration: 0.2
  }} className={`hidden lg:block h-full flex-shrink-0 backdrop-blur-xl border-r p-4 space-y-4 overflow-y-auto ${isLightTheme ? 'bg-gray-50 border-gray-200' : isHighContrast ? 'bg-white border-black' : isNeonTheme ? 'bg-black/90 border-emerald-500/30' : 'bg-black/20 border-white/10'}`} style={{
    width: showLeftSidebar ? '256px' : '0'
  }} data-id="element-510">
        {/* Component Library */}
        <div style={{
      display: showLeftSidebar ? 'block' : 'none'
    }} data-id="element-511">
            <div className="flex items-center justify-between mb-3" data-id="element-512">
                <div className={`text-xs font-bold uppercase ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-513">
                    Components ({components.length})
                </div>
                <button className={`p-1 rounded transition-colors ${isLightTheme ? 'hover:bg-gray-200' : 'hover:bg-white/10'}`} onClick={() => setShowLeftSidebar(false)} // Toggle Sidebar
        data-id="element-514">
                    <Minimize2 className={`w-4 h-4 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-515" />
                </button>
            </div>
            <div className="space-y-2" data-id="element-516">
                {components.map(comp => <button key={comp.name} onClick={() => setSelectedComponent(comp.name)} className={`w-full text-left px-3 py-3 rounded-lg text-sm transition-all flex items-center gap-3 ${selectedComponent === comp.name ? isLightTheme ? 'bg-emerald-50 border border-emerald-200' : 'bg-emerald-500/10 border border-emerald-500/30' : isLightTheme ? 'bg-gray-100 hover:bg-gray-200' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-517">
                        <Layers className={`w-4 h-4 ${selectedComponent === comp.name ? 'text-emerald-400' : isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-518" />
                        <div className="flex-1" data-id="element-519">
                            <div className={`font-medium ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-520">
                                {comp.name}
                            </div>
                            <div className={`text-xs ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-521">
                                {comp.variants} variants
                            </div>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${comp.status === 'ready' ? 'bg-emerald-500' : 'bg-gray-500'}`} data-id="element-522" />
                    </button>)}
            </div>
        </div>

        {/* AI Suggestions */}
        <div className={`pt-4 border-t ${isLightTheme ? 'border-gray-200' : 'border-white/10'}`} style={{
      display: showLeftSidebar ? 'block' : 'none'
    }} data-id="element-523">
            <div className={`text-xs font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-524">
                AI Suggestions
            </div>
            <div className="space-y-2" data-id="element-525">
                {aiSuggestions.map(suggestion => {
          const Icon = suggestion.icon;
          return <button key={suggestion.title} className={`w-full p-3 rounded-lg border cursor-pointer transition-all text-left shadow-sm ${isLightTheme ? `bg-${suggestion.color}-50 border-${suggestion.color}-200 hover:bg-${suggestion.color}-100` : `bg-${suggestion.color}-900/10 border-${suggestion.color}-500/30 hover:bg-${suggestion.color}-900/20`}`} data-id="element-526">
                            <div className="flex items-center gap-2 mb-1" data-id="element-527">
                                <Icon className={`w-3 h-3 text-${suggestion.color}-400`} data-id="element-528" />
                                <div className={`text-xs font-medium text-${suggestion.color}-400`} data-id="element-529">
                                    {suggestion.title}
                                </div>
                                <div className={`ml-auto text-xs px-2 py-0.5 rounded bg-${suggestion.color}-500/20 text-${suggestion.color}-400 font-semibold`} data-id="element-530">
                                    {suggestion.impact}
                                </div>
                            </div>
                            <div className={`text-xs ${isLightTheme ? 'text-gray-700' : 'text-white/60'}`} data-id="element-531">
                                {suggestion.description}
                            </div>
                        </button>;
        })}
            </div>
        </div>
    </motion.div>;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.98
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.98
  }} className={`h-full flex flex-col ${isLightTheme ? 'bg-gradient-to-br from-white to-gray-100' : 'bg-gradient-to-br from-gray-900 to-black'}`} data-id="element-532">
      {/* Toolbar */}
      <div className={`flex items-center justify-between px-4 md:px-6 py-3 md:py-4 backdrop-blur-xl border-b z-10 ${isLightTheme ? 'bg-white/95 border-gray-200' : 'bg-black/40 border-white/10'}`} data-id="element-533">
        {/* Left */}
        <div className="flex items-center gap-2 md:gap-4" data-id="element-534">
            {/* Left Sidebar Toggle (Only visible if sidebar is hidden) */}
            {!showLeftSidebar && <button onClick={() => setShowLeftSidebar(true)} className={`hidden lg:block p-1.5 rounded-lg transition-colors ${isLightTheme ? 'hover:bg-gray-100' : 'hover:bg-white/5'}`} aria-label="Show Components" data-id="element-535">
                    <PanelLeft className={`w-5 h-5 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-536" />
                </button>}

          <button onClick={onBack} className={`flex items-center gap-2 transition-colors touch-manipulation p-1.5 rounded-lg ${isLightTheme ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-white/60 hover:text-white hover:bg-white/5'}`} data-id="element-537">
            <ArrowLeft className="w-4 h-4" data-id="element-538" />
            <span className="hidden sm:inline text-sm" data-id="element-539">Back</span>
          </button>
          
          <div className={`hidden md:block w-px h-6 ${isLightTheme ? 'bg-gray-200' : 'bg-white/10'}`} data-id="element-540" />
          <div className="flex items-center gap-2" data-id="element-541">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/40`} data-id="element-542">
              <Code className="w-4 h-4 text-white" data-id="element-543" />
            </div>
            <div data-id="element-544">
              <div className={`text-sm font-bold ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-545">
                VibeCoding Studio
              </div>
              <div className={`text-xs ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-546">
                Component Library
              </div>
            </div>
          </div>
        </div>

        {/* Center - View Selector (Desktop) */}
        <div className={`hidden md:flex items-center gap-1 p-1 rounded-xl shadow-inner ${isLightTheme ? 'bg-gray-100' : 'bg-white/5'}`} data-id="element-547">
          {views.map(view => {
          const Icon = view.icon;
          const isActive = activeView === view.id;
          return <button key={view.id} onClick={() => setActiveView(view.id)} className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all touch-manipulation ${isActive ? 'text-white' : isLightTheme ? 'text-gray-600 hover:text-gray-900' : 'text-white/50 hover:text-white/80'}`} data-id="element-548">
                {isActive && <motion.div layoutId="activeView" className={`absolute inset-0 rounded-lg bg-gradient-to-r ${view.color} shadow-md`} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-549" />}
                <div className="relative z-10 flex items-center gap-2" data-id="element-550">
                  <Icon className="w-4 h-4" data-id="element-551" />
                  {view.label}
                </div>
              </button>;
        })}
        </div>
        
        {/* Right - Actions */}
        <div className="flex items-center gap-2" data-id="element-552">
          <button onClick={() => setShowAIChat(!showAIChat)} className={`p-2 rounded-lg transition-colors touch-manipulation ${isLightTheme ? 'bg-gray-100 hover:bg-gray-200' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-553">
            <MessageSquare className={`w-4 h-4 ${isLightTheme ? 'text-gray-600' : showAIChat ? 'text-emerald-400' : 'text-white/60'}`} data-id="element-554" />
          </button>
          <button className={`px-3 md:px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium flex items-center gap-2 hover:scale-105 transition-transform text-sm shadow-md shadow-emerald-500/40`} data-id="element-555">
            <Zap className="w-4 h-4" data-id="element-556" />
            <span className="hidden md:inline" data-id="element-557">Deploy Vibe</span>
          </button>
        </div>
      </div>

      {/* Mobile View Selector */}
      <div className={`md:hidden flex items-center gap-2 px-4 py-3 border-b overflow-x-auto ${isLightTheme ? 'bg-gray-100 border-gray-200' : 'bg-black/20 border-white/10'}`} data-id="element-558">
        {views.map(view => {
        const Icon = view.icon;
        const isActive = activeView === view.id;
        return <button key={view.id} onClick={() => setActiveView(view.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all touch-manipulation ${isActive ? `bg-gradient-to-r ${view.color} text-white shadow-sm` : isLightTheme ? 'bg-white border border-gray-300 text-gray-700' : 'bg-white/5 text-white/50 hover:bg-white/10'}`} data-id="element-559">
              <Icon className="w-4 h-4" data-id="element-560" />
              {view.label}
            </button>;
      })}
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden" data-id="element-561">
        
        {/* Left Sidebar - Component Library (Desktop/Toggleable) */}
        <LeftSidebar data-id="element-562" />

        {/* Center - Main View */}
        <div className="flex-1 flex flex-col min-w-0" data-id="element-563">
          <div className="flex-1 p-4 md:p-6 overflow-y-auto" data-id="element-564">
            <AnimatePresence mode="wait" data-id="element-565">
              <motion.div key={activeView} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.3
            }} className="h-full" data-id="element-566">
                {/* Design View (Focus op de component zelf) */}
                {activeView === 'design' && <div className="space-y-6" data-id="element-567">
                    <h2 className={`text-2xl md:text-3xl font-bold ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-568">
                        {selectedComponent} - Design Canvas
                    </h2>
                    <div className={`backdrop-blur-xl rounded-2xl border p-6 shadow-xl ${isLightTheme ? 'bg-white border-gray-200' : 'bg-white/5 border-white/10'}`} data-id="element-569">
                      <h3 className={`text-lg font-bold mb-4 ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-570">
                        Visual Variants
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-571">
                        {/* Variant 1 */}
                        <div className={`p-4 rounded-xl border ${isLightTheme ? 'bg-gray-50 border-gray-200' : 'bg-white/5 border-white/10'}`} data-id="element-572">
                          <div className={`text-xs font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-573">
                            Primary
                          </div>
                          <button className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium shadow-md shadow-emerald-500/40 hover:scale-[1.03] transition-transform" data-id="element-574">
                            {selectedComponent}
                          </button>
                        </div>
                        {/* Variant 2 */}
                        <div className={`p-4 rounded-xl border ${isLightTheme ? 'bg-gray-50 border-gray-200' : 'bg-white/5 border-white/10'}`} data-id="element-575">
                          <div className={`text-xs font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-576">
                            Secondary
                          </div>
                          <button className={`w-full px-6 py-3 rounded-lg font-medium transition-all ${isLightTheme ? 'bg-gray-200 text-gray-900 hover:bg-gray-300' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-577">
                            {selectedComponent}
                          </button>
                        </div>
                        {/* Variant 3 */}
                        <div className={`p-4 rounded-xl border ${isLightTheme ? 'bg-gray-50 border-gray-200' : 'bg-white/5 border-white/10'}`} data-id="element-578">
                          <div className={`text-xs font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-579">
                            Ghost
                          </div>
                          <button className={`w-full px-6 py-3 rounded-lg font-medium border-2 transition-all ${isLightTheme ? 'border-gray-300 text-gray-900 hover:bg-gray-100' : 'border-white/20 text-white hover:bg-white/5'}`} data-id="element-580">
                            {selectedComponent}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Code View (Met Code Header) */}
                {activeView === 'code' && <div className={`h-full backdrop-blur-xl rounded-2xl border flex flex-col overflow-hidden ${isLightTheme ? 'bg-gray-50 border-gray-200' : 'bg-black/50 border-white/10'}`} data-id="element-581">
                    {/* Code Header */}
                    <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${isLightTheme ? 'bg-gray-100 border-gray-200' : 'bg-black/30 border-white/10'}`} data-id="element-582">
                      <div className="flex items-center gap-2" data-id="element-583">
                        <Code className={`w-4 h-4 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-584" />
                        <span className={`text-sm font-mono ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-585">
                          {selectedComponent}.tsx
                        </span>
                      </div>
                      <button className={`p-1.5 rounded transition-colors ${isLightTheme ? 'hover:bg-gray-200' : 'hover:bg-white/10'}`} data-id="element-586">
                        <Copy className={`w-4 h-4 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-587" />
                      </button>
                    </div>
                    {/* Code Content */}
                    <div className="p-6 font-mono text-sm overflow-auto flex-1" data-id="element-588">
                      <pre className={isLightTheme ? 'text-gray-900' : 'text-white/80'} data-id="element-589">
                        {codeExample}
                      </pre>
                    </div>
                  </div>}
                
                {/* Preview View (Meer focus op thema/vibe) */}
                {activeView === 'preview' && <div className={`h-full rounded-2xl border p-8 flex flex-col items-center justify-center ${isLightTheme ? 'bg-white border-gray-200' : 'bg-black border-white/10'}`} data-id="element-590">
                    <div className="text-center space-y-6" data-id="element-591">
                      <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full mx-auto bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center shadow-2xl shadow-cyan-500/40`} data-id="element-592">
                        <Eye className="w-8 h-8 text-white" data-id="element-593" />
                      </div>
                      <h3 className={`text-2xl font-bold ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-594">
                        Live Vibe Render
                      </h3>
                      <p className={`max-w-md mx-auto ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-595">
                        Bekijk het thema en de componenten in een gesimuleerde app-omgeving.
                      </p>
                      <div className="flex gap-4 justify-center pt-4" data-id="element-596">
                        <button className="px-6 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:scale-105 transition-transform shadow-md shadow-emerald-500/40" data-id="element-597">
                          Primary Button
                        </button>
                        <button className="px-6 py-3 rounded-lg bg-white/10 text-white font-medium hover:bg-white/20 transition-colors" data-id="element-598">
                          Secondary
                        </button>
                      </div>
                    </div>
                  </div>}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Right Sidebar - AI Chat (Conditional) */}
        <AnimatePresence data-id="element-599">
          {showAIChat && <motion.div initial={{
          x: 320,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: 320,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className={`w-80 flex-shrink-0 backdrop-blur-xl border-l flex flex-col ${isLightTheme ? 'bg-gray-50 border-gray-200' : 'bg-black/20 border-white/10'}`} data-id="element-600">
              <div className={`p-4 border-b flex-shrink-0 ${isLightTheme ? 'border-gray-200' : 'border-white/10'}`} data-id="element-601">
                <div className="flex items-center justify-between" data-id="element-602">
                  <div className={`text-sm font-bold ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-603">
                    AI Assistant
                  </div>
                  <button onClick={() => setShowAIChat(false)} className={`transition-colors text-xl leading-none ${isLightTheme ? 'text-gray-600 hover:text-gray-900' : 'text-white/60 hover:text-white'}`} data-id="element-604">
                    &times;
                  </button>
                </div>
              </div>
              <div className="flex-1 p-4 overflow-y-auto space-y-4" data-id="element-605">
                <div className={`p-3 rounded-lg text-sm ${isLightTheme ? 'bg-emerald-50 text-gray-900 border border-emerald-200' : 'bg-emerald-500/10 text-white/80 border border-emerald-500/20'}`} data-id="element-606">
                  <div className={`font-medium mb-1 flex items-center gap-2 ${isLightTheme ? 'text-emerald-600' : 'text-emerald-400'}`} data-id="element-607">
                    <Sparkles className="w-3 h-3" data-id="element-608" />
                    AI
                  </div>
                  Ik zie dat je aan de **{selectedComponent}** component werkt. Wil
                  je dat ik een toegankelijkheidscheck doe?
                </div>
                <div className={`p-3 rounded-lg text-sm ml-auto max-w-[80%] ${isLightTheme ? 'bg-gray-200 text-gray-900' : 'bg-white/10 text-white/80'}`} data-id="element-609">
                  Ja graag, check vooral de ARIA labels
                </div>
                <div className={`p-3 rounded-lg text-sm ${isLightTheme ? 'bg-emerald-50 text-gray-900 border border-emerald-200' : 'bg-emerald-500/10 text-white/80 border border-emerald-500/20'}`} data-id="element-610">
                  <div className={`font-medium mb-2 flex items-center gap-2 ${isLightTheme ? 'text-emerald-600' : 'text-emerald-400'}`} data-id="element-611">
                    <Check className="w-3 h-3" data-id="element-612" />
                    Analyse compleet
                  </div>
                  <ul className="space-y-1 text-xs" data-id="element-613">
                    <li data-id="element-614">✓ Button heeft duidelijke labels</li>
                    <li data-id="element-615">✓ Focus states zijn goed gedefinieerd</li>
                    <li data-id="element-616">⚠️ Overweeg keyboard navigation toe te voegen</li>
                  </ul>
                </div>
              </div>
              <div className={`p-4 border-t flex-shrink-0 ${isLightTheme ? 'border-gray-200' : 'border-white/10'}`} data-id="element-617">
                <input type="text" placeholder="Vraag iets aan AI..." className={`w-full rounded-lg px-4 py-3 text-sm transition-colors focus:outline-none ${isLightTheme ? 'bg-gray-100 border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-emerald-500' : 'bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-emerald-500'}`} data-id="element-618" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}